import dayjs from 'dayjs'
import { ConsentSendForm } from '@lmc/common/types'
import { postJSON } from '../../../../../common/services/api'
import { showToast } from '../../../../../common/utils'
import { Resident } from '@lmc/ts-common'

type FormState = {
  ability: boolean
  capacity: boolean
  name: string
  relationship: string
  imageURL: string
  hasConfirmed: boolean
  lpaValue: any
  comment: string
  reason: string
}

export const generateLogText = (
  resident: Resident,
  formState: FormState,
  consentForm: ConsentSendForm
) => {
  const { capacity, ability, comment, reason, name, relationship } = formState

  const residentName = resident?.preferredName || resident.name.first
  const pronoun = resident.gender === 'male' ? 'he' : 'she'

  let title: string
  let description: string

  if (capacity) {
    if (ability) {
      title = `Signed by ${residentName}`
      description = `${residentName} confirms that ${pronoun} approves the ${consentForm.title} statement.`
    } else {
      title = `Signed by ${name}`
      description = `${residentName} confirms that ${pronoun} approves the ${consentForm.title} statement. Witnessed and signed by ${name} (${relationship}).`
    }
  } else {
    title = `Signed by ${name}`
    description = `${name} (${relationship}) signed the ${consentForm.title} statement on behalf of ${residentName}.`
  }

  if (reason) description += ` ${residentName} did not sign because: ${reason}.`
  if (comment) description += ` ${comment}.`

  return { title, description }
}

type onSubmitType = {
  resident: Resident
  nextReviewDate: any
  consents?: ConsentSendForm[]
  formState: FormState
  consentSingleForm?: ConsentSendForm
  isSingleSign?: boolean
  item?: string
  category?: string
  // eslint-disable-next-line consistent-return
}

export const onSubmit = async ({
  resident,
  nextReviewDate,
  consents,
  formState,
  consentSingleForm,
  isSingleSign,
  item,
  category
}: // eslint-disable-next-line consistent-return
onSubmitType) => {
  try {
    const { imageURL } = formState

    if (!isSingleSign) {
      // eslint-disable-next-line consistent-return
      const signatures = await Promise.all(
        // @ts-ignore
        consents.map(async (consentForm) => {
          const { title, description } = generateLogText(
            resident,
            formState,
            consentForm
          )

          const log = {
            resident: resident.id,
            title,
            description,
            hidden: true,
            item,
            category
          }

          const res = await postJSON({
            url: 'list/logs',
            json: log
          })

          const form = new FormData()
          const file = dataURItoBlob(imageURL)

          // @ts-ignore
          form.append('picture1', file)

          await postJSON({
            url: `list/logs/${res.result.id}`,
            method: 'PUT',
            contentType: 'multipart/form-data',
            body: form // log,
          })

          return {
            ...consentForm,
            log: res.result.id
          }
        })
      )

      // eslint-disable-next-line no-return-await
      const res = await postJSON({
        url: 'list/consent-forms/sign',
        json: {
          resident: resident.id,
          nextReviewDate:
            nextReviewDate && dayjs(nextReviewDate, 'DD-MM-YYYY').toISOString(),
          consents: signatures
        }
      })

      return res
    }

    const { title, description } = generateLogText(
      resident,
      formState,
      // @ts-ignore
      consentSingleForm
    )

    const log = {
      resident: resident.id,
      title,
      description,
      hidden: true,
      item,
      category
    }

    const res = await postJSON({
      url: 'list/logs',
      json: log
    })

    const form = new FormData()
    const file = dataURItoBlob(imageURL)
    // @ts-ignore
    form.append('picture1', file)

    const updatedLog = await postJSON({
      url: `list/logs/${res.result.id}`,
      method: 'PUT',
      contentType: 'multipart/form-data',
      body: form
    })

    const signResult = await postJSON({
      url: `list/consent-forms/${consentSingleForm?.id}/sign`,
      json: {
        isRefused: false,
        log: updatedLog.result.id
      }
    })

    return signResult
  } catch (e) {
    showToast({
      title: 'Oops! Something went wrong!',
      type: 'error'
    } as any)
    // eslint-disable-next-line no-console
    console.log(e)
  }
}

export function dataURItoBlob(dataURI: any) {
  try {
    // convert base64/URLEncoded data component to raw binary data held in a string
    let byteString
    if (dataURI.split(',')[0].indexOf('base64') >= 0)
      byteString = atob(dataURI.split(',')[1])
    else byteString = unescape(dataURI.split(',')[1])

    // separate out the mime component
    // const mimeString = dataURI.split(',')[0].split(':')[1].split('')[0]

    // write the bytes of the string to a typed array
    const ia = new Uint8Array(byteString.length)
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }
    // const file = new Blob([ia], { type: mimeString })
    // const uri = URL.createObjectURL(file)
    // return {
    //     uri,
    //     type: 'image/png',
    //     name: `signature.png`
    // }
    return new Blob([ia], { type: 'image/png' })
  } catch (e) {
    return null
  }
}

export const shortCutBuilder = (input: string) => {
  const titleStringArray = input?.split(' ')
  const singleWord = titleStringArray?.length === 1
  if (singleWord) {
    return input?.toUpperCase()?.charAt(0)
  }

  const lastWordOfArray = titleStringArray?.slice(-1)[0]

  const secondWord = titleStringArray?.slice(
    lastWordOfArray === '' ? -2 : -1
  )[0]

  const shortCutBuilder = `${titleStringArray
    ?.shift()
    ?.charAt(0)
    ?.toUpperCase()}${secondWord
    ?.charAt(secondWord.startsWith('(') ? 1 : 0)
    ?.toUpperCase()}`
  return shortCutBuilder
}

export const setNextReviewDateByOptions = (
  timeOptionNextReview: string,
  setNextReviewDate: any,
  form: any
) => {
  if (timeOptionNextReview === '3 months') {
    const date = dayjs().add(3, 'months').endOf('day')
    setNextReviewDate(date)
    form.setFieldsValue({
      'date-picker': date
    })
  } else if (timeOptionNextReview === '6 months') {
    const date = dayjs().add(6, 'months').endOf('day')
    setNextReviewDate(date)
    form.setFieldsValue({
      'date-picker': date
    })
  } else if (timeOptionNextReview === '1 year') {
    const date = dayjs().add(1, 'years').endOf('day')
    setNextReviewDate(date)
    form.setFieldsValue({
      'date-picker': date
    })
  }
}

export const getTimeOptions = (translate: any) => [
  {
    label: translate('threeMonth'),
    value: '3 months'
  },
  {
    label: translate('sixMonths'),
    value: '6 months'
  },
  {
    label: translate('oneYear'),
    value: '1 year'
  }
]
